import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogTitle } from '@scio3/ui';
import type { AppMeta } from '@scio3/shared-types';
import { PrehookDashboard } from './PrehookDashboard';
import { PrehookPreview } from './PrehookPreview';
import { PrehookAppIcon } from './PrehookAppIcon';
import { preventImplicitDismiss } from './prehook-dismiss';
import '../prehook.css';

/**
 * The pre-hook modal — the first thing a `/signUp?app=<app>` visitor sees once the
 * intent cookie is consumed, in front of the app's real add-modal.
 *
 * Outside clicks and Escape are deliberately inert — `preventImplicitDismiss`
 * (`./prehook-dismiss.ts`) carries the why; the two buttons are the only exits.
 *
 * Generic by construction: only the icon and the interpolated name change between
 * apps, both read off the `AppMeta` the launcher already resolved. There is no
 * Power-BI-specific asset or copy anywhere in this file.
 *
 * Geometry, colours and motion reproduce `claude-dev/2026-09-10-dashboard-fast-path/
 * design/prehook-modal.dc.html` with repo primitives; the keyframes live in
 * `../prehook.css`. Pre-hook V3 (FR-19) dropped that mock's two highlight elements and the
 * CTA's tinted lift — the scene now reads flatter than the original design on purpose;
 * everything else (tile motion, dashboard keyframes, floor/bezel shadow) is unchanged.
 */

interface PrehookDialogProps {
  appMeta: AppMeta | null;
  onConnect: () => void;
  onDismiss: () => void;
}

export function PrehookDialog({ appMeta, onConnect, onDismiss }: PrehookDialogProps) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Focus the dialog itself on open, not the CTA.
   *
   * This modal appears on its own after signup, before the visitor has touched the page, so
   * Radix focusing the CTA programmatically is enough for Chrome to mark it `:focus-visible`
   * — whether the CTA's own ring is visible there or not, the state is real. When the CTA
   * hands off to the app's add-modal, the control Radix focuses next INHERITS
   * `:focus-visible` (that is the CSS rule for programmatic focus), and the smallapp section
   * header it lands on has no focus-visible style of its own — so Chrome painted its default
   * blue outline there. Measured, 2026-09-11 (predates the pre-hook V3 CTA restyle, FR-19).
   *
   * Focusing the content instead breaks the chain at the source: the container is not a
   * control, so nothing inherits, and the CTA is then reached by a plain mouse click, which
   * never sets `:focus-visible`. Focus is still inside the dialog and still trapped, so
   * keyboard and screen-reader behaviour is unchanged — Radix does exactly this by default
   * for dialogs whose content holds no focusable element.
   */
  const focusDialogItself = useCallback((event: Event) => {
    event.preventDefault();
    contentRef.current?.focus({ preventScroll: true });
  }, []);

  if (!appMeta) return null;

  // `AppMeta` has no `name`; `text` is the display name every other consumer uses
  // (ConfigEngineDialog, SmallAppEditDialog, useOpenAppModal all read `text`).
  const appName = appMeta.text ?? appMeta.appType ?? '';
  const headline = t('Put Your {{appName}} Dashboard on Any Screen', { appName });

  return (
    <Dialog open>
      <DialogContent
        ref={contentRef}
        data-testid="app-intent-prehook"
        aria-describedby={undefined}
        hideCloseButton
        onOpenAutoFocus={focusDialogItself}
        // `flex flex-col` replaces the base `grid`: a grid `auto` track does not shrink under
        // `max-height`, it overflows and the base `overflow-hidden` clips whatever is past the
        // edge — here, the only two exits, since every implicit dismiss is disabled below.
        // 95vh rather than the usual 85vh: the scene (392px) plus the ask (~265px) is ~657px
        // tall, so 85vh would already scroll at 1280x720 — the viewport this design was drawn
        // for. 95vh clears it there (684px) and still caps + scrolls on a genuinely short
        // viewport (a 125%-zoomed laptop, devtools open, phone landscape). Same 95vh band as
        // EngageWizardDialog / DesignerEditDialog.
        // `focus:outline-none` because `onOpenAutoFocus` above focuses this container: it is a
        // wrapper, not a control, so Chrome's default ring around the whole modal would be
        // noise. Radix's own examples do the same on dialog content.
        className="flex max-h-[95vh] flex-col p-0 focus:outline-none sm:max-w-3xl"
        onPointerDownOutside={preventImplicitDismiss}
        onInteractOutside={preventImplicitDismiss}
        onEscapeKeyDown={preventImplicitDismiss}
      >
        {/* The visible headline is an <h2> below; this keeps Radix's a11y contract
            without a second visible title, as ConfigEngineDialog does. */}
        <DialogTitle className="sr-only">{headline}</DialogTitle>

        {/* The one scroll surface. `min-h-0` so it may shrink below its content height when the
            cap engages; the scene keeps its fixed 392px inside (this is a block box, not a
            flex one, so nothing in the design is squashed — it scrolls instead). */}
        <div className="min-h-0 overflow-y-auto">
          {/* `.prehook-scene` is where the four dashboard tints and the bezel/screen
              colours are declared (see prehook.css). */}
          <div className="prehook-scene relative h-[392px] overflow-hidden bg-secondary">
            <div
              aria-hidden
              className="prehook-floor absolute left-1/2 top-[356px] h-[26px] w-[440px] -translate-x-1/2 rounded-[50%]"
            />

            {/* Near-square corners on purpose: a real TV frame carries a couple of
                millimetres of radius at most, and the design's 14px read as a tablet.
                The panel itself is square. Radii here are the TV, NOT the design system's
                surface tokens, so they are literal like the bezel colour above. */}
            <div className="prehook-bezel absolute left-1/2 top-[30px] w-[564px] -translate-x-1/2 rounded-[3px] px-2.5 pb-[18px] pt-2.5">
              <div className="prehook-screen relative h-[304px] w-[544px] overflow-hidden rounded-none">
                <PrehookDashboard />
                {/* The real screenshot, for the apps we have one for, fading in over the
                    placeholder. The placeholder stays mounted underneath on purpose: it is
                    what the visitor watches until this decodes, and what they keep watching
                    for an app with no screenshot or a fetch that fails. */}
                <PrehookPreview appMeta={appMeta} />
              </div>
              {/* The logo tile straddles the screen's bottom-left corner — a badge ON the
                  TV rather than something playing on it. Deliberately a sibling of the
                  screen and a child of the BEZEL: the screen is `overflow-hidden`, so a
                  tile inside it could not hang past the edge. It sat centred over the
                  screen while that screen was an abstract animation and covering the
                  middle cost nothing; once a real report renders there, the middle is the
                  most convincing part of the picture (PM call, 2026-09-11). */}
              <div className="prehook-tile absolute left-10 top-[250px] flex h-[92px] w-[92px] items-center justify-center rounded-[20px]">
                <PrehookAppIcon appMeta={appMeta} size={52} />
              </div>
              <div
                aria-hidden
                className="prehook-power absolute bottom-[5px] left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full"
              />
            </div>
          </div>

          <div className="flex flex-col items-center px-6 pb-[30px] pt-9 text-center sm:px-[84px]">
            {/* No <br> (i18n values may not carry markup): the max-width does the wrapping.
                Measured at 32px/600/-0.022em in the app's Inter stack, "Put Your Power BI
                Dashboard" is 403px and the same plus "on" is 444px, so a column anywhere in
                [403, 444) breaks exactly where the design breaks. 420px sits inside it.
                Deliberately NOT `text-balance`: balance minimises the longest line, which moves
                the break up to after the app name ("Put Your Power BI" / "Dashboard on Any
                Screen"). Other languages simply wrap where they must in the same column. */}
            <h2 className="max-w-[420px] text-[32px] font-semibold leading-[1.14] tracking-[-0.022em] text-foreground">
              {headline}
            </h2>
            <Button
              data-testid="app-intent-prehook-cta"
              size="xl"
              className="mt-7 w-full max-w-[400px]"
              onClick={onConnect}
            >
              {t('Connect {{appName}}', { appName })}
            </Button>
            <Button
              data-testid="app-intent-prehook-dismiss"
              variant="ghost"
              className="mt-3 text-muted-foreground"
              onClick={onDismiss}
            >
              {t('Not now')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
